import * as THREE from "three";
import { Mission } from "./mission";
import {
  ConvoyIntercept,
  TrappedSurvival,
  DestroyPrimaries,
  HoldOut,
  NeutralizeAll,
  SquadReachDestination,
  SquadSurvival,
  TimeLimit,
} from "./objectives";
import type { Objective } from "./objectives";
import type { EnemyAnchorDef, MissionConfig, WaveDef } from "./types";
import type { RouteNode } from "../world/nav";

type ObjectiveDef = {
  type: string;
  label?: string;
  minSurvivors?: number;
  seconds?: number;
  maxEscapes?: number;
};

type WaveDefJson = {
  trigger: "immediate" | "waypointReached" | "threshold";
  waypointIndex?: number;
  enemiesAlive?: number;
  count: number;
  origin: [number, number, number];
  spread: number;
};

type ValidatedMissionJson = {
  id: string;
  name: string;
  mapId?: MissionConfig["mapId"];
  squadMode?: MissionConfig["squadMode"];
  winObjectives: ObjectiveDef[];
  failObjectives: ObjectiveDef[];
  waves: WaveDefJson[];
  squadRoute?: RouteNode[];
  enemyAnchors?: EnemyAnchorDef[];
  reinforcementBudget?: number;
  briefing?: string[];
};

const VALID_WAVE_TRIGGERS = new Set<WaveDef["trigger"]>(["immediate", "waypointReached", "threshold"]);
const VALID_MAP_IDS = new Set<MissionConfig["mapId"]>(["current", "river-valley", "iron-market", "green-hammer", "iron-curtain", "arctic-wolf"]);
const VALID_SQUAD_MODES = new Set<MissionConfig["squadMode"]>(["advance", "hold"]);
const VALID_ANCHOR_SOURCES = new Set<EnemyAnchorDef["source"]>(["structure", "cave", "building", "nest", "vehicle", "ridge", "fallback"]);
const VALID_ANCHOR_POSTURES = new Set<NonNullable<EnemyAnchorDef["posture"]>>(["assault", "garrison", "ambush"]);

function buildObjective(def: ObjectiveDef): Objective {
  switch (def.type) {
    case "SquadReachDestination":
      return new SquadReachDestination(def.label);
    case "SquadSurvival":
      return new SquadSurvival(def.minSurvivors ?? 1, def.label);
    case "NeutralizeAll":
      return new NeutralizeAll(def.label);
    case "HoldOut":
      if (def.seconds === undefined) throw new Error("HoldOut objective requires seconds");
      return new HoldOut(def.seconds, def.label);
    case "TimeLimit":
      if (def.seconds === undefined) throw new Error("TimeLimit objective requires seconds");
      return new TimeLimit(def.seconds, def.label);
    case "DestroyPrimaries":
      return new DestroyPrimaries(def.label);
    case "ConvoyIntercept":
      return new ConvoyIntercept(def.maxEscapes ?? 0, def.label);
    case "TrappedSurvival":
      return new TrappedSurvival(def.label);
    default:
      throw new Error(`Unknown objective type: ${def.type}`);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function asNonEmptyString(value: unknown, path: string): string {
  if (typeof value !== "string" || value.trim() === "") {
    throw new Error(`Mission JSON invalid ${path}: expected non-empty string`);
  }
  return value;
}

function asNumber(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`Mission JSON invalid ${path}: expected finite number`);
  }
  return value;
}

function asNonNegativeInteger(value: unknown, path: string, min = 0): number {
  const n = asNumber(value, path);
  if (!Number.isInteger(n) || n < min) {
    throw new Error(`Mission JSON invalid ${path}: expected integer >= ${min}`);
  }
  return n;
}

function parseObjective(value: unknown, path: string): ObjectiveDef {
  if (!isRecord(value)) {
    throw new Error(`Mission JSON invalid ${path}: expected object`);
  }

  const type = asNonEmptyString(value.type, `${path}.type`);
  const label = value.label === undefined ? undefined : asNonEmptyString(value.label, `${path}.label`);
  const minSurvivors = value.minSurvivors === undefined
    ? undefined
    : asNonNegativeInteger(value.minSurvivors, `${path}.minSurvivors`, 1);
  const seconds = value.seconds === undefined
    ? undefined
    : asNumber(value.seconds, `${path}.seconds`);
  const maxEscapes = value.maxEscapes === undefined
    ? undefined
    : asNonNegativeInteger(value.maxEscapes, `${path}.maxEscapes`);

  const parsed: ObjectiveDef = { type };
  if (label !== undefined) parsed.label = label;
  if (minSurvivors !== undefined) parsed.minSurvivors = minSurvivors;
  if (seconds !== undefined) parsed.seconds = seconds;
  if (maxEscapes !== undefined) parsed.maxEscapes = maxEscapes;
  return parsed;
}

function parseOrigin(value: unknown, path: string): [number, number, number] {
  if (!Array.isArray(value) || value.length !== 3) {
    throw new Error(`Mission JSON invalid ${path}: expected [x,y,z]`);
  }

  return [
    asNumber(value[0], `${path}[0]`),
    asNumber(value[1], `${path}[1]`),
    asNumber(value[2], `${path}[2]`),
  ];
}

function parseWave(value: unknown, path: string): WaveDefJson {
  if (!isRecord(value)) {
    throw new Error(`Mission JSON invalid ${path}: expected object`);
  }

  const triggerValue = value.trigger;
  if (typeof triggerValue !== "string" || !VALID_WAVE_TRIGGERS.has(triggerValue as WaveDef["trigger"])) {
    throw new Error(`Mission JSON invalid ${path}.trigger: expected one of immediate|waypointReached|threshold`);
  }
  const trigger = triggerValue as WaveDef["trigger"];

  const parsed: WaveDefJson = {
    trigger,
    count: asNonNegativeInteger(value.count, `${path}.count`, 1),
    origin: parseOrigin(value.origin, `${path}.origin`),
    spread: asNumber(value.spread, `${path}.spread`),
  };

  if (parsed.spread < 0) {
    throw new Error(`Mission JSON invalid ${path}.spread: expected number >= 0`);
  }

  if (trigger === "waypointReached") {
    parsed.waypointIndex = asNonNegativeInteger(value.waypointIndex, `${path}.waypointIndex`);
  } else if (value.waypointIndex !== undefined) {
    throw new Error(`Mission JSON invalid ${path}.waypointIndex: only allowed for waypointReached`);
  }

  if (trigger === "threshold") {
    parsed.enemiesAlive = asNonNegativeInteger(value.enemiesAlive, `${path}.enemiesAlive`);
  } else if (value.enemiesAlive !== undefined) {
    throw new Error(`Mission JSON invalid ${path}.enemiesAlive: only allowed for threshold`);
  }

  return parsed;
}

function parseRouteNode(value: unknown, path: string): RouteNode {
  if (!isRecord(value)) {
    throw new Error(`Mission JSON invalid ${path}: expected object`);
  }

  const origin = parseOrigin(value.position, `${path}.position`);
  const arrivalRadius = asNumber(value.arrivalRadius, `${path}.arrivalRadius`);
  if (arrivalRadius <= 0) {
    throw new Error(`Mission JSON invalid ${path}.arrivalRadius: expected number > 0`);
  }

  const parsed: RouteNode = {
    position: new THREE.Vector3(origin[0], origin[1], origin[2]),
    arrivalRadius,
    label: asNonEmptyString(value.label, `${path}.label`),
  };

  if (value.corridorWidth !== undefined) {
    const corridorWidth = asNumber(value.corridorWidth, `${path}.corridorWidth`);
    if (corridorWidth <= 0) {
      throw new Error(`Mission JSON invalid ${path}.corridorWidth: expected number > 0`);
    }
    parsed.corridorWidth = corridorWidth;
  }

  if (value.holdTime !== undefined) {
    const holdTime = asNumber(value.holdTime, `${path}.holdTime`);
    if (holdTime < 0) {
      throw new Error(`Mission JSON invalid ${path}.holdTime: expected number >= 0`);
    }
    parsed.holdTime = holdTime;
  }

  if (value.holdUntilClear !== undefined) {
    if (typeof value.holdUntilClear !== "boolean") {
      throw new Error(`Mission JSON invalid ${path}.holdUntilClear: expected boolean`);
    }
    parsed.holdUntilClear = value.holdUntilClear;
  }

  if (value.holdRadius !== undefined) {
    const holdRadius = asNumber(value.holdRadius, `${path}.holdRadius`);
    if (holdRadius <= 0) {
      throw new Error(`Mission JSON invalid ${path}.holdRadius: expected number > 0`);
    }
    parsed.holdRadius = holdRadius;
  }

  if (value.callout !== undefined) {
    parsed.callout = asNonEmptyString(value.callout, `${path}.callout`);
  }

  return parsed;
}

function parseEnemyAnchor(value: unknown, path: string): EnemyAnchorDef {
  if (!isRecord(value)) {
    throw new Error(`Mission JSON invalid ${path}: expected object`);
  }

  const triggerValue = value.trigger;
  if (typeof triggerValue !== "string" || !VALID_WAVE_TRIGGERS.has(triggerValue as WaveDef["trigger"])) {
    throw new Error(`Mission JSON invalid ${path}.trigger: expected one of immediate|waypointReached|threshold`);
  }

  const sourceValue = value.source;
  if (typeof sourceValue !== "string" || !VALID_ANCHOR_SOURCES.has(sourceValue as EnemyAnchorDef["source"])) {
    throw new Error(`Mission JSON invalid ${path}.source: expected supported source type`);
  }

  const parsed: EnemyAnchorDef = {
    id: asNonEmptyString(value.id, `${path}.id`),
    source: sourceValue as EnemyAnchorDef["source"],
    trigger: triggerValue as WaveDef["trigger"],
    count: asNonNegativeInteger(value.count, `${path}.count`, 1),
    origin: parseOrigin(value.origin, `${path}.origin`),
    spread: asNumber(value.spread, `${path}.spread`),
  };

  if (parsed.spread < 0) {
    throw new Error(`Mission JSON invalid ${path}.spread: expected number >= 0`);
  }

  if (parsed.trigger === "waypointReached") {
    parsed.waypointIndex = asNonNegativeInteger(value.waypointIndex, `${path}.waypointIndex`);
  } else if (value.waypointIndex !== undefined) {
    throw new Error(`Mission JSON invalid ${path}.waypointIndex: only allowed for waypointReached`);
  }

  if (parsed.trigger === "threshold") {
    parsed.enemiesAlive = asNonNegativeInteger(value.enemiesAlive, `${path}.enemiesAlive`);
  } else if (value.enemiesAlive !== undefined) {
    throw new Error(`Mission JSON invalid ${path}.enemiesAlive: only allowed for threshold`);
  }

  if (value.posture !== undefined) {
    if (typeof value.posture !== "string" || !VALID_ANCHOR_POSTURES.has(value.posture as NonNullable<EnemyAnchorDef["posture"]>)) {
      throw new Error(`Mission JSON invalid ${path}.posture: expected assault|garrison|ambush`);
    }
    parsed.posture = value.posture as NonNullable<EnemyAnchorDef["posture"]>;
  }

  if (value.activateRadius !== undefined) {
    const activateRadius = asNumber(value.activateRadius, `${path}.activateRadius`);
    if (activateRadius <= 0) {
      throw new Error(`Mission JSON invalid ${path}.activateRadius: expected number > 0`);
    }
    parsed.activateRadius = activateRadius;
  }

  if (value.reinforcementEligible !== undefined) {
    if (typeof value.reinforcementEligible !== "boolean") {
      throw new Error(`Mission JSON invalid ${path}.reinforcementEligible: expected boolean`);
    }
    parsed.reinforcementEligible = value.reinforcementEligible;
  }

  if (value.facingDeg !== undefined) {
    parsed.facingDeg = asNumber(value.facingDeg, `${path}.facingDeg`);
  }

  if (value.fireArcDeg !== undefined) {
    const fireArcDeg = asNumber(value.fireArcDeg, `${path}.fireArcDeg`);
    if (fireArcDeg <= 0 || fireArcDeg > 360) {
      throw new Error(`Mission JSON invalid ${path}.fireArcDeg: expected number > 0 and <= 360`);
    }
    parsed.fireArcDeg = fireArcDeg;
  }

  if (parsed.fireArcDeg !== undefined && parsed.facingDeg === undefined) {
    throw new Error(`Mission JSON invalid ${path}.facingDeg: required when fireArcDeg is provided`);
  }

  return parsed;
}

function validateMissionJson(json: unknown): ValidatedMissionJson {
  if (!isRecord(json)) {
    throw new Error("Mission JSON invalid root: expected object");
  }

  const id = asNonEmptyString(json.id, "id");
  const name = asNonEmptyString(json.name, "name");

  let mapId: MissionConfig["mapId"] | undefined;
  if (json.mapId !== undefined) {
    if (typeof json.mapId !== "string" || !VALID_MAP_IDS.has(json.mapId as MissionConfig["mapId"])) {
      throw new Error(`Mission JSON invalid mapId: expected ${[...VALID_MAP_IDS].join("|")}`);
    }
    mapId = json.mapId as MissionConfig["mapId"];
  }

  let squadMode: MissionConfig["squadMode"] | undefined;
  if (json.squadMode !== undefined) {
    if (typeof json.squadMode !== "string" || !VALID_SQUAD_MODES.has(json.squadMode as MissionConfig["squadMode"])) {
      throw new Error("Mission JSON invalid squadMode: expected advance|hold");
    }
    squadMode = json.squadMode as MissionConfig["squadMode"];
  }

  if (!Array.isArray(json.winObjectives) || json.winObjectives.length === 0) {
    throw new Error("Mission JSON invalid winObjectives: expected non-empty array");
  }
  if (!Array.isArray(json.failObjectives)) {
    throw new Error("Mission JSON invalid failObjectives: expected array");
  }

  const winObjectives = json.winObjectives.map((value, idx) => parseObjective(value, `winObjectives[${idx}]`));
  const failObjectives = json.failObjectives.map((value, idx) => parseObjective(value, `failObjectives[${idx}]`));

  if (json.waves !== undefined && !Array.isArray(json.waves)) {
    throw new Error("Mission JSON invalid waves: expected array");
  }

  const waves = (json.waves ?? []).map((value, idx) => parseWave(value, `waves[${idx}]`));
  if (json.squadRoute !== undefined && !Array.isArray(json.squadRoute)) {
    throw new Error("Mission JSON invalid squadRoute: expected array");
  }
  const squadRoute = (json.squadRoute ?? []).map((value, idx) => parseRouteNode(value, `squadRoute[${idx}]`));
  if (json.enemyAnchors !== undefined && !Array.isArray(json.enemyAnchors)) {
    throw new Error("Mission JSON invalid enemyAnchors: expected array");
  }
  const enemyAnchors = (json.enemyAnchors ?? []).map((value, idx) => parseEnemyAnchor(value, `enemyAnchors[${idx}]`));

  let reinforcementBudget: number | undefined;
  if (json.reinforcementBudget !== undefined) {
    reinforcementBudget = asNonNegativeInteger(json.reinforcementBudget, "reinforcementBudget");
  }

  let briefing: string[] | undefined;
  if (json.briefing !== undefined) {
    if (!Array.isArray(json.briefing)) {
      throw new Error("Mission JSON invalid briefing: expected array of strings");
    }
    briefing = json.briefing.map((line, idx) => asNonEmptyString(line, `briefing[${idx}]`));
  }

  const validated: ValidatedMissionJson = { id, name, winObjectives, failObjectives, waves };
  if (mapId !== undefined) validated.mapId = mapId;
  if (squadMode !== undefined) validated.squadMode = squadMode;
  if (squadRoute.length > 0) validated.squadRoute = squadRoute;
  if (enemyAnchors.length > 0) validated.enemyAnchors = enemyAnchors;
  if (reinforcementBudget !== undefined) validated.reinforcementBudget = reinforcementBudget;
  if (briefing !== undefined) validated.briefing = briefing;
  return validated;
}

export function loadMission(json: unknown): MissionConfig {
  const parsed = validateMissionJson(json);

  const mission = new Mission(
    parsed.id,
    parsed.name,
    parsed.winObjectives.map(buildObjective),
    parsed.failObjectives.map(buildObjective),
  );

  const waves: WaveDef[] = parsed.waves.map((w) => {
    const def: WaveDef = { trigger: w.trigger, count: w.count, origin: w.origin, spread: w.spread };
    if (w.waypointIndex !== undefined) def.waypointIndex = w.waypointIndex;
    if (w.enemiesAlive !== undefined) def.enemiesAlive = w.enemiesAlive;
    return def;
  });

  const config: MissionConfig = {
    id: parsed.id,
    name: parsed.name,
    mapId: parsed.mapId ?? "current",
    squadMode: parsed.squadMode ?? "advance",
    mission,
    waves,
  };

  if (parsed.squadRoute !== undefined) config.squadRoute = parsed.squadRoute;
  if (parsed.enemyAnchors !== undefined) config.enemyAnchors = parsed.enemyAnchors;
  if (parsed.reinforcementBudget !== undefined) config.reinforcementBudget = parsed.reinforcementBudget;
  if (parsed.briefing !== undefined) config.briefing = parsed.briefing;
  return config;
}
